import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';

import noop from 'lodash-es/noop';

import { baseApiClient } from 'api/BaseApiClient';
import type { TenantCompanyResponse } from 'api/types';
import { useGetTenantCompanies } from 'hooks/company';
import { useFeatures } from 'hooks/features';
import { CookieKeys, cookieService } from 'shared/lib/cookies/cookieService';

type TenancyContextType = {
  tenantCompanyId: string;
  setTenantCompanyId: (id: string) => void;
  tenantCompany?: TenantCompanyResponse;
};

const defaultContextValue: TenancyContextType = {
  tenantCompanyId: '',
  setTenantCompanyId: noop,
  tenantCompany: undefined,
};

export const TenancyContext = createContext(defaultContextValue);

export const TenancyContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data: features } = useFeatures();
  const hasMultitenancy = !!features?.flags?.multitenancy;

  /**
   * Initialize from cookies but don't set in baseApiClient - Auth0Context handles that
   * We use Auth0Context because we need the header set in baseApiClient before any request is made including prefetching
   */
  const [tenantCompanyIdState, setTenantCompanyIdState] = useState(
    () => cookieService.get(CookieKeys.TENANT_COMPANY_ID) || '',
  );
  const { data: tenantCompanies } = useGetTenantCompanies();

  const tenantCompany = tenantCompanies?.find((company) => company.id === tenantCompanyIdState) ?? tenantCompanies?.[0];

  const setTenantCompanyId = useCallback(
    (id: string, options: { withReload?: boolean } = { withReload: true }) => {
      if (hasMultitenancy) {
        cookieService.set(CookieKeys.TENANT_COMPANY_ID, id);
        baseApiClient.setTenantCompanyId(id);
        setTenantCompanyIdState(id);

        // Replace current location with hard navigation to /app to fetch profile, properties, etc. for a new company
        if (options.withReload) {
          window.location.href = '/app';
        }
      }
    },
    [hasMultitenancy],
  );

  useEffect(() => {
    if (!!tenantCompanies?.length && !tenantCompanyIdState) {
      setTenantCompanyId(tenantCompanies[0].id, { withReload: false });
    }
  }, [tenantCompanies, setTenantCompanyId, tenantCompanyIdState]);

  return (
    <TenancyContext.Provider value={{ tenantCompanyId: tenantCompanyIdState, setTenantCompanyId, tenantCompany }}>
      {children}
    </TenancyContext.Provider>
  );
};
